import * as S from '../physics/schwarzschild';
import { BlackHoleScene } from '../render/blackhole/BlackHoleScene';
import type { Chapter, ChapterContext } from './types';
import { CAPTIONS, REAL } from '../data/act5';
import { LETTER, type Line } from '../data/letter';
import { showCard, clearCard, esc } from '../ui/forms';
import { makePostcard } from '../ui/postcard';

type Phase = 'idle' | 'arrival' | 'approach' | 'choice' | 'hold' | 'fall' | 'ending' | 'done';
const smooth = (t: number) => { t = Math.max(0, Math.min(1, t)); return t * t * (3 - 2 * t); };
const PIT_META = LETTER.chapters.find((c) => c.id === 'pit')!;
const expert = (id: keyof typeof CAPTIONS): Line => ({ voice: 'expert', text: CAPTIONS[id].l });

/**
 * The last chapter: Sagittarius A*. Scripted arrival, free approach, the choice at the last stable orbit,
 * the hold or the fall, the message home with the two clocks.
 */
export class PitChapter implements Chapter {
  readonly id = 'pit';
  readonly title = 'All the way';
  readonly world = new BlackHoleScene();
  get scene() { return this.world.scene; }
  get camera() { return this.world.camera; }

  private ctx!: ChapterContext;
  private phase: Phase = 'idle';
  private pt = 0;
  private rate = 1;
  private diving = false;
  private idle = 0;
  private fall: S.InfallState | null = null;
  private earthTick = 0;
  private shipTick = 0;
  private capturePending = false;
  private snapshotPending = false;
  private lastLight: HTMLCanvasElement | null = null;
  private postcard: HTMLCanvasElement | null = null;
  private holdStartDist = 12;
  private userEl = 0.1;
  private choice: 'hold' | 'fall' | null = null;
  private startEarth = 0;
  private startShip = 0;
  private reduceMotion = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  readonly TIME_SCALE = 120;
  readonly FALL_SCALE = 2.2;
  readonly HOLD_REAL_SECONDS = 24;
  /** the innermost stable circular orbit: 6 GM/c², three Schwarzschild radii */
  readonly CHOICE_R = 6;
  readonly wantsClocks = true;
  readonly description = 'The black hole Sagittarius A* seen up close: a glowing disk of gas bent over and under the dark circle of its shadow; the two clocks compare your time with Earth\'s.';
  private lastT = 0; private lastW = 1; private lastH = 1;

  setQuality(cinematic: boolean) { this.world.setQuality(cinematic); }

  enter(ctx: ChapterContext) {
    this.ctx = ctx;
    this.diving = false; this.idle = 0; this.fall = null; this.earthTick = 0; this.shipTick = 0; this.capturePending = false; this.snapshotPending = false;
    this.lastLight = null; this.postcard = null; this.userEl = 0.1; this.choice = null;
    this.startEarth = ctx.clock.earthSeconds; this.startShip = ctx.clock.shipSeconds;
    const o = this.world.orbit; o.az = 0.3; o.el = 0.1; o.dist = 60; o.tilt = 0;
    const meta = LETTER.chapters.find((c) => c.id === 'pit')!;
    this.setPhase('arrival');
    ctx.say(meta.intro);
    ctx.hud.setHint('Arriving. You’ll get the controls in a moment.');
  }

  exit() { this.ctx.hud.setLever(null); this.ctx.hud.setMove(null); this.ctx.hud.setHint(''); clearCard(this.ctx.overlay); this.phase = 'idle'; }

  private setPhase(p: Phase) {
    this.phase = p; this.pt = 0;
    const { hud, audio } = this.ctx, o = this.world.orbit;
    if (p === 'approach') { this.lever(); hud.setMove(PIT_META.lever); hud.setHint(''); this.ctx.say([expert('steer')]); }
    if (p === 'choice') { hud.clearSay(); this.diving = false; hud.setLever({ kind: 'buttons', note: 'The last orbit that holds.', items: [{ label: 'Park for 30 days', onClick: () => this.choose('hold') }, { label: 'Let go and fall', primary: true, onClick: () => this.choose('fall') }] }); hud.setMove('Park here for a thirty-day survey and head home, or let go and fall in. Pick one.'); hud.setHint(''); hud.setDemo(null); this.ctx.say([expert('choice')]); audio.event('choice'); this.snapshotPending = true; }
    if (p === 'hold') { hud.clearSay(); hud.setLever(null); hud.setMove(null); this.holdStartDist = o.dist; hud.setHint('Parked. Watch the two clocks.'); this.ctx.say([expert('hold')]); this.choice = 'hold'; this.ctx.run.choice = 'hold'; audio.event('hold'); }
    if (p === 'fall') { hud.clearSay(); hud.setLever(null); hud.setMove(null); this.fall = { r: o.dist, r0: o.dist, tau: 0, t: 0, v: 0 }; hud.setHint('Falling. Watch your clock.'); this.ctx.say([expert('fall')]); this.choice = 'fall'; this.ctx.run.choice = 'fall'; audio.event('letgo'); }
    if (p === 'ending') { hud.setLever(null); this.capturePending = true; hud.setHint(''); audio.event(this.choice === 'fall' ? 'cross' : 'ending-hold'); }
  }

  private lever() {
    this.ctx.hud.setLever({ kind: 'buttons', items: [{ label: 'Dive', pressed: this.diving, onClick: () => this.toggleDive() }] });
  }

  private toggleDive() {
    if (this.phase !== 'approach') return;
    this.diving = !this.diving; this.ctx.hud.setLeverPressed('Dive', this.diving);
    if (this.diving) this.ctx.audio.event('dive');
  }

  private choose(which: 'hold' | 'fall') { if (this.phase === 'choice') this.setPhase(which); }

  private elevationFor(dist: number): number { const s = smooth((30 - dist) / 18); return Math.max(-0.5, Math.min(1.1, this.userEl + 0.4 * s)); }
  private fovFor(dist: number): number { return 0.82 + 0.5 * smooth((30 - dist) / 22); }

  onDrag(dx: number, dy: number) {
    if (this.phase !== 'approach' && this.phase !== 'choice' && this.phase !== 'hold') return;
    const o = this.world.orbit; o.az -= dx * 0.005; this.userEl = Math.max(-0.5, Math.min(0.7, this.userEl + dy * 0.004)); this.idle = 0;
  }
  onWheel(deltaY: number) {
    if (this.phase !== 'approach') return;
    const o = this.world.orbit; o.dist = Math.max(this.CHOICE_R, Math.min(60, o.dist * (1 + deltaY * 0.0012)));
    this.diving = false; this.ctx.hud.setLeverPressed('Dive', false); this.idle = 0;
  }
  onClick() { /* no aim lever here */ }

  /** The accessible route: take the ship to the last orbit that holds; the choice there is still the reader's. */
  demonstrate() {
    if (this.phase === 'arrival') { this.pt = Math.max(this.pt, 13.5); return; }
    if (this.phase === 'approach' && !this.diving) this.toggleDive();
  }

  /** A deliberately composed frame for the postcard: the disk and the shadow in view, whatever the ship was doing. */
  private composedFrame(): HTMLCanvasElement {
    const o = this.world.orbit, saved = { az: o.az, el: o.el, dist: o.dist, tilt: o.tilt };
    o.dist = 9; o.el = 0.26; o.tilt = 0;
    this.world.setFov(this.fovFor(o.dist));
    this.world.update(this.lastT, this.lastW, this.lastH);
    this.ctx.composer.render();
    const c = document.createElement('canvas'); c.width = this.ctx.canvas.width; c.height = this.ctx.canvas.height;
    c.getContext('2d')?.drawImage(this.ctx.canvas, 0, 0);
    Object.assign(o, saved);
    this.world.setFov(this.fovFor(o.dist));
    this.world.update(this.lastT, this.lastW, this.lastH);
    return c;
  }

  update(dt: number, _t: number, width: number, height: number) {
    const { hud, audio, clock } = this.ctx, o = this.world.orbit;
    this.pt += dt; this.idle += dt;
    let missionDt = 0, shipDt = 0;
    if (this.phase === 'arrival') {
      const s = smooth(this.pt / 14);
      o.dist = 60 - 30 * s; o.az += dt * 0.05; o.el = 0.1 + 0.03 * Math.sin(this.pt * 0.3);
      this.rate = S.staticRate(o.dist); missionDt = dt * this.TIME_SCALE; shipDt = missionDt * this.rate;
      if (this.pt > 7) this.ctx.say([expert('disk')]);
      if (this.pt >= 14) this.setPhase('approach');
    } else if (this.phase === 'approach') {
      if (this.idle > 1.5 && !this.reduceMotion) o.az += dt * 0.035;
      if (this.diving) o.dist = Math.max(this.CHOICE_R, o.dist - dt * 1.3);
      o.el = this.elevationFor(o.dist) + (this.idle > 1.5 && !this.reduceMotion ? 0.04 * Math.sin(this.pt * 0.11) : 0);
      this.rate = S.staticRate(o.dist); missionDt = dt * this.TIME_SCALE; shipDt = missionDt * this.rate;
      if (o.dist < 22 && this.pt > 2) this.ctx.say([expert('fold')]);
      if (o.dist < 15) this.ctx.say([expert('clocks')]);
      if (o.dist <= this.CHOICE_R + 1e-6) this.setPhase('choice');
    } else if (this.phase === 'choice') {
      if (this.idle > 1.5 && !this.reduceMotion) o.az += dt * 0.03;
      o.el = this.elevationFor(o.dist);
      this.rate = S.staticRate(o.dist); missionDt = dt * this.TIME_SCALE; shipDt = missionDt * this.rate;
    } else if (this.phase === 'hold') {
      const settle = smooth(this.pt / 3);
      o.dist = this.holdStartDist - (this.holdStartDist - 6) * settle; o.az += dt * (0.05 + 0.25 * settle);
      o.el = this.elevationFor(o.dist) * (1 - 0.6 * settle); o.tilt = 0.75 * settle;
      this.rate = settle < 1 ? S.staticRate(o.dist) : S.orbitRate(6);
      if (settle < 1) { missionDt = dt * this.TIME_SCALE; shipDt = missionDt * this.rate; }
      else { const perReal = (REAL.holdDaysShip * 86400) / this.HOLD_REAL_SECONDS; shipDt = dt * perReal; missionDt = shipDt / this.rate; }
      if (this.pt >= 3 + this.HOLD_REAL_SECONDS) this.setPhase('ending');
    } else if (this.phase === 'fall' && this.fall) {
      const before = this.fall;
      this.fall = S.infallStep(this.fall, dt * this.FALL_SCALE);
      o.dist = this.fall.r; o.el = this.elevationFor(Math.max(o.dist, 4)); o.tilt = 0;
      const E = Math.sqrt(Math.max(1e-9, 1 - S.RS / this.fall.r0));
      this.rate = (1 - S.RS / this.fall.r) / E;
      missionDt = S.toSeconds(this.fall.t - before.t); shipDt = S.toSeconds(this.fall.tau - before.tau);
      if (this.fall.r <= S.RS + 0.01) this.setPhase('ending');
    }
    this.world.setFov(this.fovFor(o.dist) * (height > width * 1.05 ? 1.3 : 1));   // upright phone: wider, so the disk frames the shadow
    clock.advance(missionDt, shipDt);
    this.ctx.run.earthSeconds = clock.earthSeconds; this.ctx.run.shipSeconds = clock.shipSeconds; this.ctx.run.minR = Math.min(this.ctx.run.minR, o.dist);
    audio.setScene({ rate: this.rate, dist: o.dist, phase: this.phase, orbitPhase: o.az });
    this.earthTick += dt; if (this.earthTick >= 1) { this.earthTick -= 1; audio.tick('earth'); }
    this.shipTick += dt * this.rate; if (this.shipTick >= 1) { this.shipTick -= 1; if (this.rate > 0.12) audio.tick('ship'); }
    hud.setReadouts([['Distance from the hole', `${S.toRs(o.dist).toFixed(2)} Schwarzschild radii, ${(S.toKm(o.dist) / 1e6).toFixed(1)} million km`], ['Your clock runs at', `${this.rate.toFixed(3)} of Earth’s`]]);
    this.lastT = _t; this.lastW = width; this.lastH = height;
    this.world.update(_t, width, height);
  }

  afterRender() {
    if (this.snapshotPending) {
      this.snapshotPending = false;
      const c = document.createElement('canvas'); c.width = this.ctx.canvas.width; c.height = this.ctx.canvas.height;
      c.getContext('2d')?.drawImage(this.ctx.canvas, 0, 0); this.lastLight = c;
    }
    if (!this.capturePending) return;
    this.capturePending = false;
    const earth = this.ctx.clock.earthSeconds - this.startEarth, ship = this.ctx.clock.shipSeconds - this.startShip, gap = earth - ship;
    const lines = this.choice === 'hold'
      ? [`Held ${S.formatDuration(ship)} by my clock. Earth counted ${S.formatDuration(earth)}.`, `My twin is now ${S.formatDuration(gap)} older than me.`]
      : [`Crossed in ${S.formatDuration(ship)} by my clock. Earth counted ${S.formatDuration(earth)}, and is still counting.`, `Message sent from ${S.toRs(this.world.orbit.dist).toFixed(2)} Schwarzschild radii.`];
    this.postcard = makePostcard(this.composedFrame(), lines, this.choice === 'hold' ? 'Message home' : 'Last light');
    this.showEnding(earth, ship, gap);
  }

  private showEnding(earth: number, ship: number, gap: number) {
    const lover = this.choice === 'hold' ? LETTER.pitHold : LETTER.pitFall;
    const c = this.choice === 'hold' ? CAPTIONS.ending_hold : CAPTIONS.ending_fall;
    this.ctx.hud.clearSay();
    this.ctx.say([...lover, { voice: 'expert', text: c.l }]);
    this.ctx.measure('pit', LETTER.measurements.pit);
    const html = `<h2>${esc(c.h)}</h2>
      <div class="reveal">
        <div><div class="k">Earth's clock, this chapter</div><div class="v">${esc(S.formatDuration(earth))}</div></div>
        <div><div class="k">Your clock, this chapter</div><div class="v">${esc(S.formatDuration(ship))}</div></div>
        <div class="gap"><div class="k">${this.choice === 'hold' ? 'Your twin is older by' : 'Earth is ahead by'}</div><div class="v">${esc(S.formatDuration(gap))}${this.choice === 'fall' ? ' and counting' : ''}</div></div>
      </div>
      <p class="lover">${esc(lover[0].text)}</p>
      <img class="postcard" alt="Your postcard: the last frame with the two clocks" src="${this.postcard ? this.postcard.toDataURL('image/jpeg', 0.85) : ''}">`;
    showCard(this.ctx.overlay, html, [
      { label: 'Save postcard', onClick: () => this.savePostcard() },
      { label: 'Continue', primary: true, onClick: () => { clearCard(this.ctx.overlay); this.phase = 'done'; this.ctx.finish(); } }
    ]);
  }

  private savePostcard() {
    if (!this.postcard) return;
    const a = document.createElement('a'); a.href = this.postcard.toDataURL('image/jpeg', 0.9); a.download = 'bending-the-milky-way-postcard.jpg'; document.body.appendChild(a); a.click(); a.remove();
  }
}
